// キャンバス参加者間で共有するタイマーの状態と状態遷移。
// 残り時間を毎秒配信せず、終了予定時刻から各クライアントが導出できる形で保持する。

export const CANVAS_TIMER_DEFAULT_DURATION_MS = 3 * 60 * 1000;
export const CANVAS_TIMER_MIN_DURATION_MS = 1000;
export const CANVAS_TIMER_MAX_DURATION_MS = (99 * 60 + 59) * 1000;
// クライアントの再送期限。サーバ側の操作 ID 保持期間より十分短く保つ。
export const CANVAS_TIMER_COMMAND_RETRY_WINDOW_MS = 60 * 1000;

export type CanvasTimerStatus = "idle" | "paused" | "running";

export interface CanvasTimerState {
  readonly durationMs: number;
  readonly endAtEpochMs?: number | undefined;
  readonly remainingMs: number;
  readonly revision: number;
  readonly status: CanvasTimerStatus;
  readonly updatedAtEpochMs: number;
}

export type CanvasTimerCommand =
  | { readonly action: "add-minute" }
  | { readonly action: "pause" }
  | { readonly action: "reset" }
  | { readonly action: "resume" }
  | { readonly action: "start"; readonly durationMs: number };

/** 保存状態が無いキャンバスで使う初期状態を返す。 */
export function createInitialCanvasTimerState(): CanvasTimerState {
  return {
    durationMs: CANVAS_TIMER_DEFAULT_DURATION_MS,
    remainingMs: CANVAS_TIMER_DEFAULT_DURATION_MS,
    revision: 0,
    status: "idle",
    updatedAtEpochMs: 0,
  };
}

/** タイマー時間を秒単位へ丸め、画面で扱える範囲に収める。 */
export function normalizeCanvasTimerDuration(durationMs: number): number {
  if (!Number.isFinite(durationMs)) return CANVAS_TIMER_DEFAULT_DURATION_MS;
  const rounded = Math.round(durationMs / 1000) * 1000;
  return Math.min(CANVAS_TIMER_MAX_DURATION_MS, Math.max(CANVAS_TIMER_MIN_DURATION_MS, rounded));
}

/** 現在時刻における残り時間を返す。running 以外では保存済みの残り時間を使う。 */
export function getCanvasTimerRemainingMs(state: CanvasTimerState, nowEpochMs: number): number {
  const remaining =
    state.status === "running" && state.endAtEpochMs !== undefined
      ? state.endAtEpochMs - nowEpochMs
      : state.remainingMs;
  return Math.min(CANVAS_TIMER_MAX_DURATION_MS, Math.max(0, remaining));
}

/** 共有コマンドを現在状態へ適用し、次の revision の状態を返す。 */
export function applyCanvasTimerCommand(
  state: CanvasTimerState,
  command: CanvasTimerCommand,
  nowEpochMs: number
): CanvasTimerState {
  const revision = state.revision + 1;
  switch (command.action) {
    case "start": {
      const durationMs = normalizeCanvasTimerDuration(command.durationMs);
      return {
        durationMs,
        endAtEpochMs: nowEpochMs + durationMs,
        remainingMs: durationMs,
        revision,
        status: "running",
        updatedAtEpochMs: nowEpochMs,
      };
    }
    case "pause": {
      const remainingMs = getCanvasTimerRemainingMs(state, nowEpochMs);
      if (remainingMs <= 0) {
        return createFinishedCanvasTimerState(state.durationMs, revision, nowEpochMs);
      }
      return {
        durationMs: state.durationMs,
        remainingMs,
        revision,
        status: "paused",
        updatedAtEpochMs: nowEpochMs,
      };
    }
    case "resume": {
      const remainingMs = getCanvasTimerRemainingMs(state, nowEpochMs);
      if (remainingMs <= 0) {
        return createFinishedCanvasTimerState(state.durationMs, revision, nowEpochMs);
      }
      return {
        durationMs: state.durationMs,
        endAtEpochMs: nowEpochMs + remainingMs,
        remainingMs,
        revision,
        status: "running",
        updatedAtEpochMs: nowEpochMs,
      };
    }
    case "reset":
      return {
        durationMs: state.durationMs,
        remainingMs: state.durationMs,
        revision,
        status: "idle",
        updatedAtEpochMs: nowEpochMs,
      };
    case "add-minute": {
      const currentRemainingMs = getCanvasTimerRemainingMs(state, nowEpochMs);
      const remainingMs = Math.min(CANVAS_TIMER_MAX_DURATION_MS, currentRemainingMs + 60_000);
      const durationMs = Math.min(CANVAS_TIMER_MAX_DURATION_MS, state.durationMs + 60_000);
      if (state.status === "running") {
        return {
          durationMs,
          endAtEpochMs: nowEpochMs + remainingMs,
          remainingMs,
          revision,
          status: "running",
          updatedAtEpochMs: nowEpochMs,
        };
      }
      return {
        durationMs,
        remainingMs,
        revision,
        status: state.status,
        updatedAtEpochMs: nowEpochMs,
      };
    }
  }
}

/** 0到達後の状態を一意にし、終了表示・チャイム・1分追加の経路を維持する。 */
function createFinishedCanvasTimerState(
  durationMs: number,
  revision: number,
  nowEpochMs: number
): CanvasTimerState {
  return {
    durationMs,
    endAtEpochMs: nowEpochMs,
    remainingMs: 0,
    revision,
    status: "running",
    updatedAtEpochMs: nowEpochMs,
  };
}
